import { Observable, of } from 'rxjs';
import { QueryPartStrategy } from '../../advanced-search/filters/base/entities/query-part-strategy';
import { QueryPart } from '../../advanced-search/filters/base/entities/query-part.class';
import { Restriction } from '../../core/api/entities/api-query.entity';
import { Coordinate } from '../../core/common/entities/position/coordinate.class';
import { BaseNestedFieldDefinition, FieldType } from '../../core/field-definitions/entities/field-definition.entity';
import { PartialRecord } from '../../core/records/entities/partial-record.class';

export abstract class AbstractGeoSandboxService {
    abstract translate(key: string): string;

    protected abstract getRecords(spacialQuery: QueryPart): Observable<PartialRecord[]>;

    protected abstract getRestrictions(): Observable<Restriction>;

    public getGeoLocationCluster(
        positionFieldDefinition: BaseNestedFieldDefinition,
        geoCoordinate: Coordinate
    ): Observable<PartialRecord[]> {
        return this.getSpatialQuery(positionFieldDefinition, geoCoordinate.toSpacialSearch());
    }

    public getInBounds(
        positionFieldDefinition: BaseNestedFieldDefinition,
        bounds: google.maps.LatLngBounds
    ): Observable<PartialRecord[]> {
        const southWestCoordinate: Coordinate = new Coordinate(
            bounds.getSouthWest().lat(),
            bounds.getSouthWest().lng()
        );
        const nortEastCoordinate: Coordinate = new Coordinate(bounds.getNorthEast().lat(), bounds.getNorthEast().lng());

        const spatialSearchTerm: string = Coordinate.spatialSearchFromCoordinates(
            southWestCoordinate,
            nortEastCoordinate
        );

        return this.getSpatialQuery(positionFieldDefinition, spatialSearchTerm);
    }

    private getSpatialQuery(
        positionFieldDefinition: BaseNestedFieldDefinition,
        spatialSearchTerm: string
    ): Observable<PartialRecord[]> {
        const geoCoordinateField: BaseNestedFieldDefinition | undefined =
            this.findGeoCoordinateField(positionFieldDefinition);

        if (!geoCoordinateField) {
            return of([]);
        }

        const spacialQuery: QueryPart = new QueryPart({
            field: geoCoordinateField.DottedKey,
            strategy: QueryPartStrategy.MatchAll,
            values: [spatialSearchTerm]
        });

        return this.getRecords(spacialQuery);
    }

    private findGeoCoordinateField(
        positionFieldDefinition: BaseNestedFieldDefinition
    ): BaseNestedFieldDefinition | undefined {
        const geoCoordinateField: BaseNestedFieldDefinition | undefined = positionFieldDefinition.Children?.find(
            child => child.Type === FieldType.GeoCoordinateField
        );

        if (!geoCoordinateField) {
            const message: string = `GeoSandboxService:: No GeoCoordinateField child found for ${positionFieldDefinition.FlatKey} field`;

            console.warn(message);
        }

        return geoCoordinateField;
    }
}
